import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CaretLeft, CaretRight, X } from '@phosphor-icons/react';
import { supabase } from '@/lib/supabaseClient';
import {
  GALLERY_CATEGORIES,
  galleryPhotoAltText,
  resolveGalleryPhotoUrl,
} from '@/lib/galleryPhotoStorage';

const INITIAL_VISIBLE = 12;
const LOAD_MORE_STEP = 12;

const FILTER_TABS = [
  { value: 'all', label: 'All' },
  ...GALLERY_CATEGORIES,
];

const GalleryLightbox = ({ open, photos, index, onClose, onChangeIndex }) => {
  const current = photos[index];

  const goPrev = useCallback(() => {
    onChangeIndex((index - 1 + photos.length) % photos.length);
  }, [index, photos.length, onChangeIndex]);

  const goNext = useCallback(() => {
    onChangeIndex((index + 1) % photos.length);
  }, [index, photos.length, onChangeIndex]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && photos.length > 1) goPrev();
      if (e.key === 'ArrowRight' && photos.length > 1) goNext();
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, photos.length, onClose, goPrev, goNext]);

  if (!open || !current || typeof document === 'undefined') return null;

  const src = resolveGalleryPhotoUrl(current.image_url);
  const hasMultiple = photos.length > 1;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Gallery photo preview"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
        aria-label="Close"
      >
        <X size={24} weight="bold" />
      </button>

      {hasMultiple && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/90 text-[#1B2D5E] shadow-md hover:bg-white transition-colors"
          aria-label="Previous photo"
        >
          <CaretLeft size={24} weight="bold" />
        </button>
      )}

      <div
        className="relative max-w-5xl max-h-[90dvh] w-full flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={src}
          alt={galleryPhotoAltText(current)}
          className="max-w-full max-h-[80dvh] w-auto h-auto object-contain rounded-lg"
        />
        {current.caption && (
          <p className="mt-3 text-center text-sm text-white/90 font-body px-4">{current.caption}</p>
        )}
        {hasMultiple && (
          <p className="mt-2 text-xs text-white/70 font-body">
            {index + 1} of {photos.length}
          </p>
        )}
      </div>

      {hasMultiple && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/90 text-[#1B2D5E] shadow-md hover:bg-white transition-colors"
          aria-label="Next photo"
        >
          <CaretRight size={24} weight="bold" />
        </button>
      )}
    </div>,
    document.body,
  );
};

const AtithiGallerySection = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { data, error } = await supabase
        .from('gallery_photos')
        .select('*')
        .eq('is_active', true)
        .order('sort_order')
        .order('created_at', { ascending: false });

      if (cancelled) return;

      if (!error && data?.length) {
        setPhotos(data);
      } else {
        setPhotos([]);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredPhotos = useMemo(() => {
    if (activeFilter === 'all') return photos;
    return photos.filter((p) => p.category === activeFilter);
  }, [photos, activeFilter]);

  const visiblePhotos = useMemo(
    () => filteredPhotos.slice(0, visibleCount),
    [filteredPhotos, visibleCount],
  );

  const hasMore = visibleCount < filteredPhotos.length;

  const handleFilterChange = (value) => {
    setActiveFilter(value);
    setVisibleCount(INITIAL_VISIBLE);
    setLightboxIndex(null);
  };

  const openLightbox = (photo) => {
    const idx = filteredPhotos.findIndex((p) => p.id === photo.id);
    if (idx !== -1) setLightboxIndex(idx);
  };

  return (
    <section className="section-outer bg-[#F8F4EE] border-t border-gray-100" data-aos="fade-up">
      <div className="section-inner !py-12 !pb-14">
        <div className="text-center mb-8">
          <p className="section-label">GALLERY</p>
          <h2 className="section-heading mt-2">Moments From the Road</h2>
          <p className="section-subtext mt-3 max-w-xl mx-auto">
            Our fleet, happy travellers, and destinations across Gujarat.
          </p>
        </div>

        <div
          className="flex flex-wrap justify-center gap-2 mb-8"
          role="tablist"
          aria-label="Gallery categories"
        >
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={activeFilter === tab.value}
              onClick={() => handleFilterChange(tab.value)}
              className={`px-4 py-2 rounded-full text-sm font-semibold font-body transition-colors ${
                activeFilter === tab.value
                  ? 'bg-[#1B2D5E] text-white shadow-sm'
                  : 'bg-white text-[#1B2D5E] border border-gray-200 hover:border-[#E8651A] hover:text-[#E8651A]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-gray-500 font-body text-sm">Loading gallery…</p>
        ) : filteredPhotos.length === 0 ? (
          <p className="text-center text-gray-500 font-body text-sm py-8">Photos coming soon</p>
        ) : (
          <>
            <div
              className="columns-2 md:columns-3 lg:columns-4 gap-4"
              style={{ columnGap: '1rem' }}
            >
              {visiblePhotos.map((photo) => {
                const src = resolveGalleryPhotoUrl(photo.image_url);
                return (
                  <button
                    key={photo.id}
                    type="button"
                    onClick={() => openLightbox(photo)}
                    className="mb-4 w-full break-inside-avoid block rounded-xl overflow-hidden border border-gray-200/80 bg-white shadow-sm hover:shadow-md hover:border-[#E8651A]/40 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8651A]"
                    style={{ breakInside: 'avoid' }}
                  >
                    <img
                      src={src}
                      alt={galleryPhotoAltText(photo)}
                      loading="lazy"
                      className="w-full h-auto block"
                    />
                    {photo.caption && (
                      <p className="px-3 py-2 text-xs text-gray-600 text-left font-body line-clamp-2">
                        {photo.caption}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>

            {hasMore && (
              <div className="text-center mt-8">
                <button
                  type="button"
                  onClick={() => setVisibleCount((c) => c + LOAD_MORE_STEP)}
                  className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-[#1B2D5E] text-white text-sm font-semibold font-body hover:bg-[#E8651A] transition-colors"
                >
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <GalleryLightbox
        open={lightboxIndex !== null}
        photos={filteredPhotos}
        index={lightboxIndex ?? 0}
        onClose={() => setLightboxIndex(null)}
        onChangeIndex={setLightboxIndex}
      />
    </section>
  );
};

export default AtithiGallerySection;
